import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RuleSearchPayload, SearchFilter } from 'app/modules/search/payload';
import { RuleSearchV2Response, RuleSource } from 'app/modules/search/response';
import { RULE_QUERY_KEYS } from 'app/modules/rules/queries/keys';
import { selectHasReadRulesPermission } from 'app/modules/session/selectors';
import { post } from 'app/shared/utils/fetchr';
import { unionBy } from 'lodash';
import { useMemo, useReducer } from 'react';
import { useSelector } from 'react-redux';

function normalizeToLowercase(value: string | number | boolean) {
  if (typeof value === 'string') {
    return value.toLowerCase();
  }
  return value;
}

const getRuleSources = (
  data: RuleSearchV2Response,
  filters?: SearchFilter<RuleSource>[],
): RuleSource[] => {
  if (filters && filters.length > 0) {
    const filterSet = filters
      .map(({ field, phrases }) => ({
        // normalize strings
        field,
        phrases: phrases.map(normalizeToLowercase),
      }))
      .map(({ field, phrases }) => ({
        // remove duplicates
        field,
        phrases: new Set(phrases),
      }));

    return data.hits.reduce<RuleSource[]>((acc, { source }) => {
      if (
        filterSet.every(
          ({ field, phrases }) =>
            source[field] && phrases.has(normalizeToLowercase(source[field])),
        )
      ) {
        acc.push(source);
      }

      return acc;
    }, []);
  }

  return data.hits.map((hit) => hit.source);
};

export const searchRule = (payload?: RuleSearchPayload) =>
  post<RuleSearchV2Response>('/rules/search/v2', payload);

export const useRuleSearch = (
  payload?: RuleSearchPayload,
  filters?: SearchFilter<RuleSource>[],
  enabled: boolean = true,
) => {
  const queryClient = useQueryClient();
  const hasReadRulesPermission = useSelector(selectHasReadRulesPermission);

  const query = useQuery<RuleSearchV2Response, Error, RuleSource[]>({
    queryFn: () => searchRule(payload),
    queryKey: RULE_QUERY_KEYS.getRuleSearch(payload),
    enabled: Boolean(enabled && !!payload && hasReadRulesPermission),
    staleTime: 5 * 60 * 1000,
    select: (data) => getRuleSources(data, filters),
  });

  // call to manually refresh agents from cached data
  const [, refreshRules] = useReducer((x) => x + 1, 0);

  const cachedQueryData = queryClient.getQueriesData<RuleSearchV2Response>({
    queryKey: RULE_QUERY_KEYS.getRuleSearch(),
  });

  const rules = useMemo(
    () =>
      cachedQueryData.reduce<RuleSource[]>((acc, i) => {
        const [, queryData] = i;
        if (queryData) {
          return unionBy(getRuleSources(queryData, filters), acc, 'id');
        }
        return acc;
      }, []),

    [cachedQueryData, filters],
  );

  return {
    query,
    refreshRules,
    rules,
  };
};
